import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getCurrentUserId } from '../utils/currentUser';

/**
 * FOCUS SESSION — the rail's time tracker, lifted out of pages/UserDashboard.js
 * (DP56 shell pass B) where it had been the only copy. The rail now lives in a
 * shared shell on three pages, and My Desk's Time Tracking tab reads the same
 * live session, so this must sit above both. Logic is unchanged: same endpoints,
 * same 1s tick, same toasts.
 */
const API = process.env.REACT_APP_BACKEND_URL;

const FocusSessionContext = createContext(null);

export const formatTimerDisplay = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export function FocusSessionProvider({ children }) {
  const [activeFocusSession, setActiveFocusSession] = useState(null);
  const [focusSeconds, setFocusSeconds] = useState(0);

  const userId = getCurrentUserId();

  useEffect(() => {
    // The provider sits at App level, so it also mounts on /login where there
    // is no session yet. Don't ask for a session we cannot be authorised for.
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${API}/api/focus/active`, { params: { user_id: userId } });
        if (!cancelled && response.data.session) {
          setActiveFocusSession(response.data.session);
          setFocusSeconds(response.data.session.elapsed_seconds || 0);
        }
      } catch (error) {
        console.error('Error checking focus session:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  useEffect(() => {
    let interval;
    if (activeFocusSession) {
      interval = setInterval(() => setFocusSeconds(prev => prev + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [activeFocusSession]);

  const startFocusSession = useCallback(async (projectId = null, description = '', appSection = 'mydesk') => {
    try {
      const response = await axios.post(`${API}/api/focus/start`, null, {
        params: {
          user_id: userId,
          project_id: projectId || undefined,
          description: description || `Working in ${appSection}`,
          app_section: appSection,
        },
      });
      toast.success('Focus session started');
      setActiveFocusSession({
        id: response.data.session_id,
        project_id: projectId,
        description,
        app_section: appSection,
      });
      setFocusSeconds(0);
    } catch (error) {
      toast.error('Failed to start focus session');
    }
  }, [userId]);

  const stopFocusSession = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/api/focus/stop`, null, { params: { user_id: userId } });
      toast.success(response.data.message);
      setActiveFocusSession(null);
      setFocusSeconds(0);
      // My Desk's Time Tracking tab refreshes its list off this.
      window.dispatchEvent(new CustomEvent('focus-session-stopped'));
    } catch (error) {
      toast.error('Failed to stop focus session');
    }
  }, [userId]);

  return (
    <FocusSessionContext.Provider value={{
      activeFocusSession,
      focusSeconds,
      startFocusSession,
      stopFocusSession,
      formatTimerDisplay,
    }}>
      {children}
    </FocusSessionContext.Provider>
  );
}

export function useFocusSession() {
  const ctx = useContext(FocusSessionContext);
  if (!ctx) {
    throw new Error('useFocusSession must be used inside FocusSessionProvider');
  }
  return ctx;
}
